import type { ReactNode } from 'react';
import type { DateContainerDate } from '../DateContainer/DateContainer';

import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { getTestProps } from '../../lib/helpers';

import { ClockIcon, PencilSolidIcon, WorldIcon } from '../../images/shapes';

import DateContainer from '../DateContainer/DateContainer';
import Panel from '../Panel/Panel';

interface ContentObjectInformationProps {
  /**
   * Created date of content object
   */
  createdAt?: DateContainerDate;
  /**
   * Updated date of content object
   */
  updatedAt?: DateContainerDate;
  /**
   * Last published date of content object
   */
  lastPublishedAt?: DateContainerDate;
  /**
   * Content object information children
   */
  children?: ReactNode;
  /**
   * Created at label
   */
  createdAtText?: string;
  /**
   * Updated at label
   */
  updatedAtText?: string;
  /**
   * Last published label
   */
  lastPublishedText?: string;
  /**
   * The id for information container
   */
  id?: string;
  /**
   * If information is pinned
   */
  isPinned?: boolean;
  /**
   * Additional elements above id information
   */
  additionalElements?: ReactNode;
  /**
   * Additional CSS classes
   */
  additionalClasses?: string;
  /**
   * Container additional CSS classes
   */
  additionalContainerClasses?: string;
  /**
   * Children additional CSS classes
   */
  additionalChildrenClasses?: string;
  /**
   * Icon additional CSS classes
   */
  additionalIconClasses?: string;
  /**
   * Title additional CSS classes
   */
  additionalTitleClasses?: string;
  /**
   * Test id for information container
   */
  testId?: string;
}

const ContentObjectInformation = ({
  createdAt = '',
  createdAtText = '',
  updatedAt = '',
  updatedAtText = '',
  lastPublishedAt = '',
  lastPublishedText = '',
  isPinned,
  children,
  id = '',
  additionalElements,
  additionalClasses = '',
  additionalTitleClasses = '',
  additionalContainerClasses = '',
  additionalChildrenClasses,
  additionalIconClasses,
  testId = '',
}: ContentObjectInformationProps) => {
  const { t } = useTranslation();

  return (
    <Panel
      title={t('ContentForm.Information')}
      isCollapsable
      additionalContainerClasses={twMerge(
        'px-4',
        isPinned &&
          'xl:absolute xl:top-0 xl:right-0 xl:z-10 xl:rounded-tr-none xl:rounded-br-none ' +
            'xl:w-[63px]! xl:h-full',
        additionalClasses,
      )}
      additionalTitleClasses={twMerge(
        'text-lg',
        isPinned && 'xl:mr-0! xl:max-w-[32px] xl:overflow-hidden',
        additionalTitleClasses,
      )}
      additionalChildrenClasses={twMerge(additionalChildrenClasses)}
      open
      id={id}
      {...getTestProps(testId, 'container', 'testId')}
    >
      <div
        className={twMerge(
          'flex flex-col space-y-6',
          isPinned && 'xl:justify-center xl:items-center',
          additionalContainerClasses,
        )}
      >
        {additionalElements}

        <DateContainer
          date={createdAt}
          icon={
            <PencilSolidIcon
              className={twMerge(
                'w-[18px] mr-3',
                isPinned && 'xl:mr-0!',
                additionalIconClasses,
              )}
              {...getTestProps(testId, 'created-at-icon')}
            />
          }
          text={createdAtText || t('TypeDefinitionCard.Created')}
          isCompact={isPinned}
          {...getTestProps(testId, 'data-created-at', 'testId')}
        />

        <DateContainer
          icon={
            <ClockIcon
              className={twMerge(
                'w-[18px] mr-3',
                isPinned && 'xl:mr-0!',
                additionalIconClasses,
              )}
              {...getTestProps(testId, 'updated-at-icon')}
            />
          }
          text={updatedAtText || t('TypeDefinitionCard.Updated')}
          date={updatedAt}
          isCompact={isPinned}
          {...getTestProps(testId, 'data-updated-at', 'testId')}
        />

        {lastPublishedAt && (
          <DateContainer
            icon={
              <WorldIcon
                className={twMerge(
                  'w-[18px] mr-3',
                  isPinned && 'xl:mr-0!',
                  additionalIconClasses,
                )}
                {...getTestProps(testId, 'last-published-at-icon')}
              />
            }
            text={lastPublishedText || t('ObjectStatus.Published')}
            date={lastPublishedAt}
            isCompact={isPinned}
            {...getTestProps(testId, 'data-last-published-at', 'testId')}
          />
        )}
        {!isPinned && children && (
          <div {...getTestProps(testId, 'children')}>{children}</div>
        )}
      </div>
    </Panel>
  );
};

export default ContentObjectInformation;
